<script setup lang="ts">
import {
  ButtonIcon,
  DropdownMenu,
  DropdownMenuItem,
  IconComment,
  IconDots,
  IconEdit,
  IconTrash,
  IconWarning,
  PostReactions,
  PostReactionsButton,
  ReactionType as UiReactionType,
  UserGallery
} from '@hobbii/hobbii-ui';
import { computed, reactive, ref, watch } from 'vue';

import { Post } from '@/__generated__/gateway/graphql';
import PostGallery from '@/components/community/UI/PostGallery.vue';
import { generateProfileUrl } from '@/components/community/utils/helpers';
import ButtonText from '@/shared/components/UI/Buttons/ButtonText.vue';
import Image from '@/shared/components/UI/Image.vue';
import Modal from '@/shared/components/UI/Modal.vue';
import UserHeader from '@/shared/components/UI/UserHeader.vue';
import { useCommunityReactionMutations } from '@/shared/composables/community';
import { useCommunityTranslations } from '@/shared/composables/community/translations';
import { useTranslations } from '@/shared/composables/useTranslations';

import CommentList from '../../CommentList.vue';
import ContentReporter from '../../ContentReporter.vue';
import PostComposer from '../../PostComposer.vue';
import ReactionTabs from '../../ReactionTabs.vue';
import RelativeTime from '../../RelativeTime.vue';
import { useCommunityAuth, useContentModel } from '../../utils/composables';
import {
  fromUiToApiReaction,
  reactionCountsToReactionData
} from '../../utils/converter';
import { ContentModel } from '../../utils/types';

const props = defineProps<{
  post: Post;
  processing: boolean;
  createPostMutation: (model: ContentModel) => Promise<void>;
  updatePostMutation: (postId: string, model: ContentModel) => Promise<void>;
  deletePostMutation: (postId: string) => Promise<void>;
  reactionTranslations: { [key in UiReactionType]: string };
  isMobile: boolean;
}>();

const post = computed(() => props.post);

const { profile, initLogin } = useCommunityAuth();

const { t } = useTranslations();
const communityTranslations = useCommunityTranslations('UserGalleris');

const { contentModel } = useContentModel(post);

const isOwner = computed(() => props.post.author.id === profile.value?.id);

const reactionsData = computed<{
  count: number;
  types: UiReactionType[];
} | null>(() => reactionCountsToReactionData(props.post.reaction_counts));

const { mutateReaction } = useCommunityReactionMutations(post);

const setReaction = async (reaction: UiReactionType | null) => {
  if (!profile.value) {
    return initLogin();
  }

  mutateReaction(fromUiToApiReaction(reaction));
};

const showReactionsModal = ref(false);
const openReactionsModal = () => {
  if (!profile.value) {
    return initLogin();
  }

  showReactionsModal.value = true;
};

const showDropDown = ref(false);
const toggleDropDown = () => {
  if (!showDropDown.value && !profile.value) {
    return initLogin();
  }
  showDropDown.value = !showDropDown.value;
};

type ShowCommentsConfig = { show: boolean; initialFocus: boolean };
const commentConfig = reactive<ShowCommentsConfig>({
  show: false,
  initialFocus: false
});
const toggleComments = (initialFocus = false) => {
  if (initialFocus && !profile.value) {
    return initLogin();
  }

  commentConfig.initialFocus = initialFocus;
  commentConfig.show = !commentConfig.show;
};

const showEditModal = ref(false);
const openEditModal = () => {
  showEditModal.value = true;
  showDropDown.value = false;
};

const showDeleteConfirmationModal = ref(false);
const openDeleteConfirmationModal = () => {
  showDeleteConfirmationModal.value = true;
  showDropDown.value = false;
};

const updatePost = computed(() =>
  isOwner.value
    ? async (model: ContentModel) => {
        await props.updatePostMutation(post.value.id, model);
        showEditModal.value = false; // cleanup, aka. close modal etc.
      }
    : null
);

const deletePost = computed(() =>
  isOwner.value
    ? async () => {
        await props.deletePostMutation(post.value.id);
        showDeleteConfirmationModal.value = false; // cleanup, aka. close modal etc.
      }
    : null
);

const showReportModal = ref(false);
const openReportModal = () => {
  showDropDown.value = false;
  showReportModal.value = true;
};

// reset commentsConfig, if post changes
watch(post, (post, prevPost) => {
  if (post.id !== prevPost.id) {
    commentConfig.show = false;
  }
});
</script>

<template>
  <UserGallery>
    <template v-if="post.images" #gallery>
      <PostGallery :images="post.images" />
    </template>
    <template #header>
      <div class="flex items-center justify-between">
        <UserHeader :href="generateProfileUrl(post.author)">
          <template #header>
            {{ post.author.display_name }}
          </template>
          <template #subheader>
            <RelativeTime :date="post.published_at" />
          </template>
          <template #avatar>
            <Image
              img-classes="size-full rounded-full object-cover"
              :src="post.author.avatar_url"
              :alt="post.author.display_name"
            />
          </template>
        </UserHeader>
        <div>
          <DropdownMenu v-model:active="showDropDown">
            <template #button>
              <ButtonIcon
                size="md"
                label="Opinions"
                theme="light"
                :active="showDropDown"
                @click="toggleDropDown"
              >
                <IconDots
                  class="text-xl"
                  :class="{
                    'opacity-100 lg:opacity-100': showDropDown
                  }"
                />
              </ButtonIcon>
            </template>
            <template v-if="isOwner">
              <DropdownMenuItem v-if="updatePost" @click="openEditModal">
                <template #icon><IconEdit class="text-xl" /></template>
                {{ communityTranslations.text_edit_post }}
              </DropdownMenuItem>
              <DropdownMenuItem
                v-if="deletePost"
                @click="openDeleteConfirmationModal"
              >
                <template #icon><IconTrash class="text-xl" /></template>
                {{ communityTranslations.text_delete_post }}
              </DropdownMenuItem>
            </template>
            <template v-else>
              <DropdownMenuItem @click="openReportModal">
                <template #icon><IconWarning class="text-xl" /></template>
                {{ communityTranslations.text_report_post }}
              </DropdownMenuItem>
            </template>
          </DropdownMenu>

          <Modal
            v-if="deletePost"
            v-model:active="showDeleteConfirmationModal"
            size="sm"
            :close-button="false"
          >
            <div class="p-8 text-center">
              <span>{{
                communityTranslations.text_delete_post_confirmation
              }}</span>
            </div>
            <div class="flex flex-col divide-y border-t">
              <ButtonText
                theme="danger"
                :loading="processing"
                size="lg"
                full-width
                @click="deletePost"
                >{{ communityTranslations.text_delete_post }}</ButtonText
              >
              <ButtonText
                font-weight="normal"
                size="lg"
                theme="white"
                full-width
                @click="showDeleteConfirmationModal = false"
                >{{ t('community.shared.text_cancel') }}</ButtonText
              >
            </div>
          </Modal>

          <ContentReporter v-model:active="showReportModal" :target="post" />
        </div>
      </div>
    </template>

    <div class="mt-2 whitespace-pre-line">
      {{ post.body }}
    </div>

    <Modal v-if="updatePost" v-model:active="showEditModal" size="lg">
      <template #header>
        <div class="flex w-full justify-center text-xl font-bold">
          {{ communityTranslations.text_edit_post }}
        </div>
      </template>
      <div>
        <PostComposer
          v-model="contentModel"
          :placeholder="communityTranslations.text_edit_post"
          :processing="processing"
          :max-images="10"
          :body-max-length="700"
          :community-translations="communityTranslations"
          @submit="updatePost"
        />
      </div>
    </Modal>

    <template #footer>
      <div class="mt-2 flex items-center justify-between border-y py-3">
        <div>
          <template v-if="reactionsData?.count">
            <PostReactions
              :reaction-types="reactionsData.types"
              :count="reactionsData.count"
              @click="openReactionsModal"
            />
            <Modal v-model:active="showReactionsModal" size="md">
              <ReactionTabs :content="post" />
            </Modal>
          </template>
        </div>
        <button
          class="hover:underline"
          @click="toggleComments(post?.comments_count === 0)"
        >
          {{ communityTranslations.text_x_comments(post.comments_count) }}
        </button>
      </div>
    </template>

    <template #actions>
      <div class="flex gap-px py-1">
        <div class="grow">
          <PostReactionsButton
            :reaction="post.own_reaction"
            :translations="reactionTranslations"
            position="left"
            @reaction="setReaction"
            >{{ reactionTranslations.love }}</PostReactionsButton
          >
        </div>
        <div class="grow">
          <ButtonText
            theme="white"
            size="md"
            :full-width="true"
            :icon-left="true"
            @click="toggleComments(true)"
          >
            <template #icon
              ><IconComment class="text-xl text-gray-300"
            /></template>
            {{ communityTranslations.text_write_comment }}
          </ButtonText>
        </div>
      </div>
    </template>
    <template v-if="commentConfig.show" #comments>
      <CommentList
        ref="commentList"
        :post="post"
        :focus-write-area="commentConfig.initialFocus"
        :scroll-into-view="
          isMobile ? { block: 'start', behavior: 'smooth' } : undefined
        "
        :community-translations="communityTranslations"
      />
    </template>
  </UserGallery>
</template>
